/**
 * P1.1 Admin Cleanup: Evidence Viewer (Step 3)
 * 
 * READ-ONLY viewer for evidence - NO creation/upload/scrape capabilities.
 * Evidence is exclusively created via Step 1 (Sources → Wizard).
 * 
 * Features:
 * - Unified evidence table for all types (Docs + Web Snapshots)
 * - Filters and drilldowns (Evidence → Source, Evidence → Chunks, Evidence → Manifest)
 * - Refresh button (UI reload only)
 * 
 * P1.4.1: Added drilldown filter support from Sources tab
 */
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  FileText,
  Database,
  RefreshCw,
  Search,
  Shield,
  CheckCircle,
  XCircle,
  Clock,
  Eye,
  Globe,
  File,
  Link2,
  Layers,
  ChevronDown,
  ChevronUp,
  AlertTriangle,
  Info,
  ExternalLink,
  Filter,
  X
} from 'lucide-react';

const EvidenceViewer = ({ 
  isActive = false, 
  drilldownSourceId = null, 
  drilldownFilterType = null,
  onClearDrilldown = null 
}) => {
  const { getAuthHeaders } = useAuth();
  const [evidence, setEvidence] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const [filterType, setFilterType] = useState('ALL');
  const [filterStatus, setFilterStatus] = useState('ALL');
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedItem, setExpandedItem] = useState(null);
  
  // P1.4.1: Source ID filter from drilldown
  const [filterSourceId, setFilterSourceId] = useState(null);
  
  // Drilldown dialogs
  const [showChunksDialog, setShowChunksDialog] = useState(false);
  const [selectedChunks, setSelectedChunks] = useState([]);
  const [loadingChunks, setLoadingChunks] = useState(false);
  
  // P1.4.1: Apply drilldown filter when props change
  useEffect(() => {
    if (drilldownSourceId) {
      setFilterSourceId(drilldownSourceId);
      // If drilldown is for chunks, auto-expand first item with that source
      if (drilldownFilterType === 'chunks') {
        // Will be handled after evidence loads
      }
    }
  }, [drilldownSourceId, drilldownFilterType]);
  
  // P1.4.1: Clear source filter
  const clearSourceFilter = () => {
    setFilterSourceId(null);
    if (onClearDrilldown) {
      onClearDrilldown();
    }
  };

  // Fetch evidence from source_registry and kb_documents
  const fetchEvidence = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch from source_registry/list (correct endpoint)
      const sourcesRes = await fetch(`${API}/source-registry/list?page_size=100`, {
        headers: getAuthHeaders()
      });

      if (sourcesRes.ok) {
        const sourcesData = await sourcesRes.json();
        const sources = sourcesData.items || [];
        
        // Transform sources to evidence view
        const evidenceList = [];
        
        for (const source of sources) {
          // Use health endpoint to get accurate counts
          let healthData = { evidence_count: 0, chunk_count: source.chunk_count || 0 };
          try {
            const healthRes = await fetch(
              `${API}/source-registry/${source.source_id}/health`,
              { headers: getAuthHeaders() }
            );
            if (healthRes.ok) {
              healthData = await healthRes.json();
            }
          } catch (e) {
            // Fallback to source data
          }
          
          // Add source with its evidence counts from health endpoint
          evidenceList.push({
            source_id: source.source_id,
            source_name: source.title || source.name || source.source_id,
            source_type: source.source_type,
            precedence_tier: source.tier || source.precedence_tier,
            status: source.status,
            chunk_count: healthData.chunk_count || 0,
            evidence_count: healthData.evidence_count || 0,
            evidence_items: [], // Evidence details loaded on expand
            is_effective: source.is_effective,
            created_at: source.registered_at || source.created_at,
            health_status: healthData.status,
            admin_hint: healthData.admin_hint
          });
        }
        
        setEvidence(evidenceList);
        
        // Compute stats
        const totalSources = evidenceList.length;
        const totalEvidence = evidenceList.reduce((sum, e) => sum + e.evidence_count, 0);
        const totalChunks = evidenceList.reduce((sum, e) => sum + e.chunk_count, 0);
        const byType = {};
        evidenceList.forEach(e => {
          byType[e.source_type] = (byType[e.source_type] || 0) + 1;
        });
        
        setStats({
          total_sources: totalSources,
          total_evidence: totalEvidence,
          total_chunks: totalChunks,
          by_type: byType
        });
      }
    } catch (error) {
      console.error('Error fetching evidence:', error);
      toast.error('Fehler beim Laden der Evidence');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  // Lazy load only when tab is active
  useEffect(() => {
    if (isActive && !initialLoadDone) {
      fetchEvidence();
      setInitialLoadDone(true);
    }
  }, [isActive, initialLoadDone, fetchEvidence]);

  // Load chunks for a source
  const loadChunks = async (sourceId) => {
    setLoadingChunks(true);
    try {
      const response = await fetch(
        `${API}/source-registry/${sourceId}/chunks`,
        { headers: getAuthHeaders() }
      );
      
      if (response.ok) {
        const data = await response.json();
        setSelectedChunks(Array.isArray(data) ? data : (data.items || []));
        setShowChunksDialog(true);
      } else {
        toast.error('Fehler beim Laden der Chunks');
      }
    } catch (error) {
      toast.error('Fehler beim Laden der Chunks');
    } finally {
      setLoadingChunks(false);
    }
  };

  // Load evidence items for a source (on expand)
  const loadEvidenceItems = async (sourceId) => {
    try {
      const response = await fetch(
        `${API}/source-registry/${sourceId}/evidence`,
        { headers: getAuthHeaders() }
      );
      
      if (response.ok) {
        const data = await response.json();
        // Update evidence list with loaded items
        setEvidence(prev => prev.map(e => 
          e.source_id === sourceId 
            ? { ...e, evidence_items: data.items || [] }
            : e
        ));
      }
    } catch (error) {
      console.error('Error loading evidence items:', error);
    }
  };

  // Handle expand toggle
  const handleExpand = (sourceId) => {
    if (expandedItem === sourceId) {
      setExpandedItem(null);
    } else {
      setExpandedItem(sourceId);
      // Load evidence items if not yet loaded
      const item = evidence.find(e => e.source_id === sourceId);
      if (item && item.evidence_items.length === 0 && item.evidence_count > 0) {
        loadEvidenceItems(sourceId);
      }
    }
  };

  // Filter evidence
  const filteredEvidence = evidence.filter(e => {
    if (filterType && filterType !== 'ALL' && e.source_type !== filterType) return false;
    if (filterStatus && filterStatus !== 'ALL' && e.status !== filterStatus) return false;
    if (searchTerm && !e.source_name.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    return true;
  });

  // Status badge
  const getStatusBadge = (status) => {
    const config = {
      ACTIVE: { color: 'bg-green-100 text-green-800', icon: CheckCircle },
      PENDING: { color: 'bg-yellow-100 text-yellow-800', icon: Clock },
      PROCESSING: { color: 'bg-blue-100 text-blue-800', icon: RefreshCw },
      FAILED: { color: 'bg-red-100 text-red-800', icon: XCircle },
      DEPRECATED: { color: 'bg-gray-100 text-gray-800', icon: AlertTriangle }
    };
    const cfg = config[status] || config.PENDING;
    const Icon = cfg.icon;
    
    return (
      <Badge className={cfg.color}>
        <Icon className="w-3 h-3 mr-1" />
        {status}
      </Badge>
    );
  };

  // Type badge
  const getTypeBadge = (sourceType) => {
    const isWeb = ['WEB_SINGLE', 'WEB_MULTI', 'WEB_PDF'].includes(sourceType);
    return (
      <Badge variant="outline" className={isWeb ? 'text-blue-600' : 'text-purple-600'}>
        {isWeb ? <Globe className="w-3 h-3 mr-1" /> : <File className="w-3 h-3 mr-1" />}
        {sourceType}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="evidence-viewer">
      {/* READ-ONLY NOTICE BANNER */}
      <Card className="border-blue-200 bg-blue-50 dark:bg-blue-950/20">
        <CardContent className="pt-4">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <p className="font-medium text-blue-800 dark:text-blue-200">
                Evidence Viewer - Nur Einsicht
              </p>
              <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                Evidence wird ausschließlich über <strong>Step 1 (Sources → "Source & Evidence hinzufügen")</strong> erstellt.
                Diese Seite dient nur zur Einsicht und Diagnose.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 dark:bg-purple-900 rounded-lg">
                <Shield className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats?.total_sources || 0}</p>
                <p className="text-sm text-slate-500">Sources</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
                <FileText className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats?.total_evidence || 0}</p>
                <p className="text-sm text-slate-500">Evidence Items</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 dark:bg-green-900 rounded-lg">
                <Database className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats?.total_chunks || 0}</p>
                <p className="text-sm text-slate-500">Chunks</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 dark:bg-orange-900 rounded-lg">
                <Globe className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats?.by_type?.WEB_SINGLE || 0 + stats?.by_type?.WEB_MULTI || 0}</p>
                <p className="text-sm text-slate-500">Web Snapshots</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters and Refresh */}
      <div className="flex gap-4 flex-wrap items-center">
        <div className="flex-1 min-w-[200px]">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Suchen..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
            />
          </div>
        </div>
        
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="Typ" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">Alle Typen</SelectItem>
            <SelectItem value="DOCUMENT">Dokument</SelectItem>
            <SelectItem value="WEB_SINGLE">Web Single</SelectItem>
            <SelectItem value="WEB_MULTI">Web Multi</SelectItem>
            <SelectItem value="WEB_PDF">Web PDF</SelectItem>
          </SelectContent>
        </Select>
        
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-[130px]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">Alle Status</SelectItem>
            <SelectItem value="ACTIVE">Active</SelectItem>
            <SelectItem value="PENDING">Pending</SelectItem>
            <SelectItem value="PROCESSING">Processing</SelectItem>
            <SelectItem value="FAILED">Failed</SelectItem>
          </SelectContent>
        </Select>
        
        <Button variant="outline" onClick={fetchEvidence} data-testid="refresh-evidence">
          <RefreshCw className="w-4 h-4 mr-2" />
          Aktualisieren
        </Button>
      </div>

      {/* Evidence Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Layers className="w-5 h-5 text-blue-600" />
            Evidence Übersicht
          </CardTitle>
          <CardDescription>
            Alle Evidence-Einträge aus dem Source Registry (Single Source of Truth)
          </CardDescription>
        </CardHeader>
        <CardContent>
          {filteredEvidence.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>Keine Evidence gefunden.</p>
              <p className="text-sm mt-2">
                Erstellen Sie Evidence über Step 1 (Sources → Wizard).
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Source</TableHead>
                  <TableHead>Typ</TableHead>
                  <TableHead>Tier</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Evidence</TableHead>
                  <TableHead>Chunks</TableHead>
                  <TableHead>Effektiv</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredEvidence.map((item) => (
                  <>
                    <TableRow key={item.source_id} data-testid={`evidence-${item.source_id}`}>
                      <TableCell>
                        <div>
                          <div className="font-medium">{item.source_name}</div>
                          <div className="text-xs text-gray-400">{item.source_id}</div>
                        </div>
                      </TableCell>
                      <TableCell>{getTypeBadge(item.source_type)}</TableCell>
                      <TableCell>
                        <Badge variant={item.precedence_tier === 'PRIMARY' ? 'default' : 'secondary'}>
                          {item.precedence_tier}
                        </Badge>
                      </TableCell>
                      <TableCell>{getStatusBadge(item.status)}</TableCell>
                      <TableCell>{item.evidence_count}</TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => loadChunks(item.source_id)}
                          disabled={loadingChunks}
                        >
                          <Database className="w-4 h-4 mr-1" />
                          {item.chunk_count}
                        </Button>
                      </TableCell>
                      <TableCell>
                        {item.is_effective ? (
                          <Badge className="bg-green-100 text-green-800">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Ja
                          </Badge>
                        ) : (
                          <Badge className="bg-red-100 text-red-800">
                            <XCircle className="w-3 h-3 mr-1" />
                            Nein
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleExpand(item.source_id)}
                        >
                          {expandedItem === item.source_id ? (
                            <ChevronUp className="w-4 h-4" />
                          ) : (
                            <ChevronDown className="w-4 h-4" />
                          )}
                        </Button>
                      </TableCell>
                    </TableRow>
                    
                    {/* Expanded Details */}
                    {expandedItem === item.source_id && (
                      <TableRow>
                        <TableCell colSpan={8} className="bg-gray-50">
                          <div className="p-4 space-y-4">
                            {/* Health Status */}
                            {item.health_status && (
                              <div className={`p-2 rounded text-sm ${
                                item.health_status === 'OK' ? 'bg-green-50 text-green-800' :
                                item.health_status === 'NO_EVIDENCE' ? 'bg-yellow-50 text-yellow-800' :
                                'bg-orange-50 text-orange-800'
                              }`}>
                                <strong>Status:</strong> {item.health_status}
                                {item.admin_hint && <span className="ml-2">- {item.admin_hint}</span>}
                              </div>
                            )}
                            
                            {/* Evidence Items */}
                            {item.evidence_count > 0 ? (
                              item.evidence_items.length > 0 ? (
                                <div>
                                  <h4 className="font-medium mb-2 flex items-center gap-2">
                                    <FileText className="w-4 h-4" />
                                    Evidence Items ({item.evidence_items.length})
                                  </h4>
                                  <div className="space-y-2">
                                    {item.evidence_items.slice(0, 5).map((ev, idx) => (
                                      <div key={idx} className="text-sm p-2 bg-white rounded border">
                                        <div className="flex items-center gap-2">
                                          <Badge variant="outline" className="text-xs">
                                            {ev.evidence_type || ev.doc_type || 'DOCUMENT'}
                                          </Badge>
                                          <span>{ev.title || ev.filename || ev.evidence_id || 'Unnamed'}</span>
                                        </div>
                                        {ev.url && (
                                          <a 
                                            href={ev.url} 
                                            target="_blank" 
                                            rel="noopener noreferrer"
                                            className="text-xs text-blue-600 flex items-center gap-1 mt-1"
                                          >
                                            <ExternalLink className="w-3 h-3" />
                                            {ev.url}
                                          </a>
                                        )}
                                      </div>
                                    ))}
                                    {item.evidence_items.length > 5 && (
                                      <p className="text-xs text-gray-500">
                                        +{item.evidence_items.length - 5} weitere...
                                      </p>
                                    )}
                                  </div>
                                </div>
                              ) : (
                                <div className="flex items-center gap-2 text-gray-500">
                                  <RefreshCw className="w-4 h-4 animate-spin" />
                                  Lade Evidence Items...
                                </div>
                              )
                            ) : (
                              <p className="text-gray-500 text-sm">Keine Evidence Items vorhanden</p>
                            )}
                            
                            {/* Metadata */}
                            <div className="text-xs text-gray-500">
                              Erstellt: {item.created_at ? new Date(item.created_at).toLocaleString('de-DE') : '-'}
                            </div>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Chunks Dialog */}
      <Dialog open={showChunksDialog} onOpenChange={setShowChunksDialog}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Database className="w-5 h-5 text-green-600" />
              Chunks Ansicht
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {selectedChunks.length === 0 ? (
              <p className="text-gray-500 text-center py-4">Keine Chunks gefunden.</p>
            ) : (
              <div className="space-y-3">
                {selectedChunks.slice(0, 20).map((chunk, idx) => (
                  <div key={idx} className="p-3 border rounded bg-gray-50">
                    <div className="flex items-center gap-2 mb-2">
                      <Badge variant="outline" className="text-xs">
                        Chunk {idx + 1}
                      </Badge>
                      {chunk.chunk_id && (
                        <span className="text-xs text-gray-400">{chunk.chunk_id}</span>
                      )}
                    </div>
                    <p className="text-sm whitespace-pre-wrap">
                      {chunk.content || chunk.text || 'No content'}
                    </p>
                  </div>
                ))}
                {selectedChunks.length > 20 && (
                  <p className="text-sm text-gray-500 text-center">
                    +{selectedChunks.length - 20} weitere Chunks
                  </p>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EvidenceViewer;
